import axios from 'axios';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

export function apiErrorMessage(error: any, fallback = 'Request failed'): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join('; ');
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return error?.message || fallback;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (email: string, username: string, password: string) =>
    api.post('/auth/register', { email, username, password }),
};

// VPN Connections API
export const vpnAPI = {
  getConnections: () => api.get('/vpn/connections'),
  createConnection: (connection: any) => api.post('/vpn/connections', connection),
  updateConnection: (id: number, connection: any) => api.put(`/vpn/connections/${id}`, connection),
  deleteConnection: (id: number) => api.delete(`/vpn/connections/${id}`),
  startConnection: (id: number) => api.post(`/vpn/connections/${id}/start`),
  stopConnection: (id: number) => api.post(`/vpn/connections/${id}/stop`),
  testConnection: (id: number) => api.post(`/vpn/connections/${id}/test`),
  getConnectionDiagnostics: (id: number) => api.get(`/vpn/connections/${id}/diagnostics`),
  getConnectionLogs: (id: number) => api.get(`/vpn/connections/${id}/logs`),
  getStatus: () => api.get('/vpn/status'),
};

// HL7 route API
export const hl7RouteAPI = {
  getStatus: () => api.get('/vpn/hl7/status'),
};

// System Management API
export const systemAPI = {
  getSystemStatus: () => api.get('/vpn/system/status'),
  startService: () => api.post('/vpn/system/start'),
  stopService: () => api.post('/vpn/system/stop'),
  restartService: () => api.post('/vpn/system/restart'),
};

// Key Management API
export const keyAPI = {
  generateKey: (keyName: string, keyType: string = 'rsa', keySize: number = 2048) =>
    api.post('/vpn/keys/generate', { key_name: keyName, key_type: keyType, key_size: keySize }),
  listKeys: () => api.get('/vpn/keys'),
  revokeKey: (keyName: string) => api.delete(`/vpn/keys/${keyName}`),
  generatePSK: (length: number = 32) => api.post('/vpn/keys/generate-psk', { length }),
};

// Backup / Restore API
export const backupAPI = {
  listBackups: () => api.get('/vpn/backups'),
  createServerBackup: () => api.post('/vpn/backups/create'),
  exportBackup: () => api.get('/vpn/backups/export', { responseType: 'blob' }),
  downloadServerBackup: (name: string) => api.get(`/vpn/backups/${encodeURIComponent(name)}/download`, { responseType: 'blob' }),
  restoreServerBackup: (name: string) => api.post(`/vpn/backups/${encodeURIComponent(name)}/restore`),
  restoreBackup: (payload: any) => api.post('/vpn/backups/restore', payload),
};
